// src/components/common/CommonModal.jsx
import React, { useRef } from 'react';
import Icon from './Icon';

const CommonModalFiles = ({
  isOpen,
  onClose,
  onSave,
  title,
  fields,
  initialData = {},
  submitText = "Save",
  cancelText = "Cancel",
  width = "600px",
  validateOnSubmit = true,
  autoComplete = "off",
  customValidate // Optional custom validation function from parent
}) => {
  const [formData, setFormData] = React.useState(initialData);
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const initialDataRef = useRef(initialData);
  const bodyRef = useRef(null);

  React.useEffect(() => {
    setFormData(initialData);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
    initialDataRef.current = initialData;
  }, [initialData, isOpen]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialDataRef.current);
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges()) {
      setShowCancelConfirm(true);
    } else {
      handleCancelConfirmed();
    }
  };

  // Handle confirmed cancel
  const handleCancelConfirmed = () => {
    setFormData(initialDataRef.current);
    setErrors({});
    setTouched({});
    setShowCancelConfirm(false);
    onClose();
  };

  // Handle close confirmation modal
  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false);
  };

  // Add this function for password complexity validation
  const validatePasswordComplexity = (password, complexityConfig) => {
    if (!password) return '';

    const {
      minLength = 8,
      requireUppercase = true,
      requireLowercase = true,
      requireNumbers = true,
      requireSpecialChars = true,
      specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?'
    } = complexityConfig;

    // Check minimum length
    if (password.length < minLength) {
      return `Password must be at least ${minLength} characters`;
    }

    // Check for uppercase
    if (requireUppercase && !/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }

    // Check for lowercase
    if (requireLowercase && !/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }

    // Check for numbers
    if (requireNumbers && !/\d/.test(password)) {
      return 'Password must contain at least one number';
    }

    // Check for special characters
    if (requireSpecialChars) {
      const specialCharsRegex = new RegExp(`[${specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);
      if (!specialCharsRegex.test(password)) {
        return `Password must contain at least one special character (${specialChars})`;
      }
    }

    return '';
  };

  // Validate a single field
  const validateField = (name, value, fieldConfig) => {
    let error = '';

    // Check if custom validation function exists
    if (customValidate && typeof customValidate === 'function') {
      const customError = customValidate(name, value, formData, fieldConfig);
      if (customError) return customError;
    }

    // Required field validation
    if (fieldConfig.required && (!value || value.toString().trim() === '')) {
      const fieldName = fieldConfig.label || name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      return `${fieldName} is required`;
    }

    // Type-specific validations
    switch (fieldConfig.type) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && value.trim() !== '' && !emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        break;

      case 'password':
        if (fieldConfig.minLength && value.length < fieldConfig.minLength) {
          return `Password must be at least ${fieldConfig.minLength} characters`;
        }
        // Add password complexity validation if field config has it
        if (fieldConfig.complexity) {
          const passwordError = validatePasswordComplexity(value, fieldConfig.complexity);
          if (passwordError) return passwordError;
        }
        break;

      case 'text':
        if (fieldConfig.maxLength && value.length > fieldConfig.maxLength) {
          const fieldName = fieldConfig.label || name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          return `${fieldName} cannot exceed ${fieldConfig.maxLength} characters`;
        }
        if (fieldConfig.minLength && value.length < fieldConfig.minLength) {
          const fieldName = fieldConfig.label || name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          return `${fieldName} must be at least ${fieldConfig.minLength} characters`;
        }
        break;

      case 'number':
        if (fieldConfig.min !== undefined && Number(value) < fieldConfig.min) {
          const fieldName = fieldConfig.label || name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          return `${fieldName} must be at least ${fieldConfig.min}`;
        }
        if (fieldConfig.max !== undefined && Number(value) > fieldConfig.max) {
          const fieldName = fieldConfig.label || name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          return `${fieldName} cannot exceed ${fieldConfig.max}`;
        }
        break;
    }

    // Custom validation function from field config
    if (fieldConfig.validate && typeof fieldConfig.validate === 'function') {
      const customError = fieldConfig.validate(value, formData);
      if (customError) return customError;
    }

    return '';
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Flatten fields array to get all individual fields
    const flattenFields = (fieldsArray) => {
      const flattened = [];
      fieldsArray.forEach(item => {
        if (Array.isArray(item)) {
          flattened.push(...item);
        } else {
          flattened.push(item);
        }
      });
      return flattened;
    };

    const allFields = flattenFields(fields);

    allFields.forEach(field => {
      if (field.name) {
        const error = validateField(field.name, formData[field.name] || '', field);
        if (error) {
          newErrors[field.name] = error;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const maxLength = 16;
    const emailMaxLength = 64;
    const usernameMaxLength = 16;

    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }

    let newValue = type === 'checkbox' ? checked : value;

    // Validate firstName and lastName length
    if (name === 'firstName' || name === 'lastName') {
      if (newValue.length > maxLength) {
        // Show error if exceeding limit
        setErrors(prev => ({
          ...prev,
          [name]: `${name === 'firstName' ? 'First name' : 'Last name'} cannot exceed ${maxLength} characters`
        }));
        // Still update the value but truncated
        newValue = newValue.slice(0, maxLength);
      } else if (errors[name]) {
        // Clear error if within limit
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
    // Validate email length
    else if (name === 'email') {
      if (newValue.length > emailMaxLength) {
        // Show error if exceeding limit
        setErrors(prev => ({
          ...prev,
          [name]: `Email cannot exceed ${emailMaxLength} characters`
        }));
        // Still update the value but truncated
        newValue = newValue.slice(0, emailMaxLength);
      } else if (errors[name]) {
        // Clear error if within limit
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
    // Validate username length and characters
    else if (name === 'username') {
      if (newValue.length > usernameMaxLength) {
        // Show error if exceeding limit
        setErrors(prev => ({
          ...prev,
          [name]: `Username cannot exceed ${usernameMaxLength} characters`
        }));
        // Still update the value but truncated
        newValue = newValue.slice(0, usernameMaxLength);
      }
      // Check for special characters or spaces in real-time
      else if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?\s]/.test(newValue)) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Username cannot contain special characters or spaces'
        }));
      }
      else if (errors[name]) {
        // Clear error if within limit and valid
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
    else if (errors[name]) {
      // Clear error for other fields
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    setFormData(prevState => ({
      ...prevState,
      [name]: newValue
    }));
  };

  const handleCustomChange = (name, value) => {
    // For custom components
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }

    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Find field config
    const findFieldConfig = (fieldsArray, fieldName) => {
      for (const item of fieldsArray) {
        if (Array.isArray(item)) {
          const found = findFieldConfig(item, fieldName);
          if (found) return found;
        } else if (item.name === fieldName) {
          return item;
        }
      }
      return null;
    };

    const fieldConfig = findFieldConfig(fields, name);

    if (fieldConfig) {
      const error = validateField(name, value, fieldConfig);
      if (error) {
        setErrors(prev => ({ ...prev, [name]: error }));
      }

      // Mark field as touched
      if (!touched[name]) {
        setTouched(prev => ({ ...prev, [name]: true }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. CommonModal's field validation FIRST
    if (validateOnSubmit) {
      const isValid = validateForm();
      if (!isValid) {
        // Mark all fields as touched to show errors
        const allTouched = {};
        const flattenFields = (fieldsArray) => {
          const flattened = [];
          fieldsArray.forEach(item => {
            if (Array.isArray(item)) flattened.push(...item);
            else flattened.push(item);
          });
          return flattened;
        };

        flattenFields(fields).forEach(field => {
          if (field.name) allTouched[field.name] = true;
        });

        setTouched(allTouched);
        return; 
      }
    }

    // 2. ONLY NOW call parent's onSave (your validation)
    setIsSubmitting(true);  // Button shows "Saving..." only for API calls

    try {
      await onSave(formData);
      onClose();  // Success - close modal
    } catch (error) {
      console.error('Error in form submission:', error);
      // Modal stays open ✅
    } finally {
      setIsSubmitting(false);  // Button back to "Upload"
    }
  };
  const handleCancel = () => {
    setFormData(initialDataRef.current);
    setErrors({});
    setTouched({});
    onClose();
  };

  if (!isOpen) return null;

  const renderField = (field) => {
    const {
      name,
      label,
      type = 'text',
      placeholder = '',
      required = false,
      options = [],
      disabled = false,
      className = '',
      min,
      max,
      step,
      rows = 3,
      component: CustomComponent
    } = field;

    const error = errors[name];
    const isTouched = touched[name];

    if (CustomComponent) {
      return (
        <div key={name} className={`form-group ${className}`}>
          <CustomComponent
            field={field}
            formData={formData}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onCustomChange={handleCustomChange}
            error={error}
            touched={isTouched}
            disabled={disabled}
            modalBodyRef={bodyRef}
          />
          {isTouched && error && (
            <div className="validation-error-message">
              {error}
            </div>
          )}
        </div>
      );
    }

    const inputProps = {
      id: name,
      name,
      value: formData[name] || '',
      onChange: handleInputChange,
      onBlur: handleBlur,
      className: `form-input ${error && isTouched ? 'input-error' : ''}`,
      placeholder,
      required,
      disabled,
      min,
      max,
      step,
      autoComplete: "off" // Add this line
    };

    switch (type) {
      case 'select':
        return (
          <div key={name} className={`form-group ${className}`}>
            {label && (
              <label htmlFor={name} className="form-label">
                {label}
                {required && <span className="required-asterisk">*</span>}
              </label>
            )}
            <select
              {...inputProps}
              autoComplete="off" // Add for all inputs
              className={`form-select ${error && isTouched ? 'input-error' : ''}`}
            >
              <option value="">Please Select</option>

              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isTouched && error && (
              <div className="validation-error-message">
                {error}
              </div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={name} className={`form-group checkbox-group ${className}`}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...inputProps}
                checked={!!formData[name]}
                autoComplete="off" // Add for all inputs
                className={error && isTouched ? 'input-error' : ''}
              />
              <span className="checkbox-custom"></span>
              {label}
              {required && <span className="required-asterisk">*</span>}
            </label>
            {isTouched && error && (
              <div className="validation-error-message">
                {error}
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={name} className={`form-group ${className}`}>
            {label && (
              <label htmlFor={name} className="form-label">
                {label}
                {required && <span className="required-asterisk">*</span>}
              </label>
            )}
            <textarea
              {...inputProps}
              className={`form-textarea ${error && isTouched ? 'input-error' : ''}`}
              rows={rows}
              autoComplete="off" // Add for select
            />
            {isTouched && error && (
              <div className="validation-error-message">
                {error}
              </div>
            )}
          </div>
        );

      case 'email':
      case 'password':
      case 'tel':
      case 'number':
      default:
        return (
          <div key={name} className={`form-group ${className}`}>
            {label && (
              <label htmlFor={name} className="form-label">
                {label}
                {required && <span className="required-asterisk">*</span>}
              </label>
            )}
            <input
              type={type}
              {...inputProps}
              autoComplete="off" // Add for all inputs
              className={`form-input ${error && isTouched ? 'input-error' : ''}`}
            />
            {isTouched && error && (
              <div className="validation-error-message">
                {error}
              </div>
            )}
          </div>
        );
    }
  };

  const renderRow = (row, rowIndex) => {
    if (Array.isArray(row)) {
      return (
        <div key={`row-${rowIndex}`} className="form-row">
          {row.map((field, colIndex) => (
            <div key={`col-${colIndex}`} className={`form-group-half ${field.className || ''}`}>
              {renderField(field)}
            </div>
          ))}
        </div>
      );
    }
    return renderField(row);
  };

  return (
    <>
      <div className="common-modal-overlay-files">
        <div className="common-modal-content" style={{ width }}>
          <div className="common-modal-header">
            <h2 className="common-modal-title">{title}</h2>
            <button
              className="common-modal-close"
              onClick={handleCancelClick}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="common-modal-form" noValidate>
            <div className="common-modal-body" ref={bodyRef}>
              {fields.map((row, index) => renderRow(row, index))}
            </div>

            <div className="common-modal-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={handleCancelClick}
                disabled={isSubmitting}
              >
                {cancelText}
              </button>
              <button
                type="submit"
                className="btn btn-save"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : submitText}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal-content">
            <div className="confirmation-modal-body">
              <Icon name="warning-icon" size={32} alt="warning-icon" />
              <h6>Discard changes</h6>
              <span className='unsaved-changes-text'> Unsaved changes will be lost</span>
            </div>
            <div className="confirmation-modal-actions">
              <button
                type="button"
                className="discard-changes-btn"
                onClick={handleCancelConfirmed}
              >
                Discard Changes
              </button>
              <button
                type="button"
                className="continue-editing-btn"
                onClick={handleCloseCancelConfirm}
              >
                Continue Editing
              </button>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommonModalFiles;